import { observable, makeObservable, flow } from "mobx";

import { EAIClient } from "Services/EAIClient";
import { appStore } from "../";
import { Faculty } from "../commonStore";
import {Utils} from "../../common/utils";


export interface Committee {
    [key: string]: number | string | boolean | Dashboard[] | Workbook[] | Faculty[] | undefined
    id: number
    name: string
    protected: boolean
    dashboards?: Dashboard[]
    workbooks?: Workbook[]
    faculty?: Faculty[]
}

export interface Dashboard {
    id: string
    tableau_id: string
    name: string
    dashboard_type: string
}

export interface Workbook {
    id: string
    name: string
    admins_can_view: boolean
    dashboard_type: string
}

class CommitteeStore {
    @observable isProcessing = false;
    @observable isUpdatingFacultiesForCommittee = false;
    @observable committees: Committee[];
    @observable committee: Committee;
    @observable committeeFilter: string;

    @observable allWorkbooks: Workbook[];
    @observable addMapCount: number;
    @observable removeMapCount: number;
    @observable hasCaughtError = false;


    client = new EAIClient();

    constructor() {
        makeObservable(this);
    }

    @flow
    * fetchCommittees(): any{
        this.isProcessing = true;
        this.hasCaughtError = false;
        try {
            const resp = yield this.client.fetchCommittees();
            this.committees = resp.data.data;
            this.isProcessing = false;
            return this.committees;
        } catch(e) {
            this.hasCaughtError = true;
            appStore.addError(e);
        }
    }

    @flow
    * fetchCommittee(committeeId: number): any{
        this.isProcessing = true;
        try {
            const resp = yield this.client.fetchCommittee(committeeId);
            this.committee = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * createCommittee(committeeName: string): any{
        this.isProcessing = true;
        try {
            const resp = yield this.client.createCommittee(committeeName);
            this.committee = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * updateCommittee(committee: Committee): any{
        this.isProcessing = true;
        try {
            yield this.client.updateCommittee(committee);
            this.fetchCommittees();
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * deleteCommittee(committeeId: number){
        this.isProcessing = true;
        try {
            yield this.client.deleteCommittee(committeeId);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * fetchAllWorkbooks(){
        this.isProcessing = true;
        try {
            const grouped = yield this.client.getDashboards(false, false, true, null, "group", false, false, false);
            let allWorkbooks = Object.keys(grouped.data.data).reduce((prev: Array<Array<any>>, cur: string) => {
                return ([...prev, ...(grouped.data.data[cur].filter((dashboard: { workbook_id: string, dashboard_type: string }) => !!dashboard.workbook_id || dashboard. dashboard_type === 'BYODashboard' ))]);
            }, []);
            allWorkbooks = allWorkbooks.map(obj => {
                return {id: obj.workbook_id || obj.id, name: obj.name, admins_can_view: obj.admins_can_view, dashboard_type: obj.dashboard_type};
            });
            this.allWorkbooks = allWorkbooks;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * removeWorkbooksFromCommittee(committeeId: number, workbookIdArr: string[]){
        this.isProcessing = true;
        try {
            // if the workbookId is a number, it is a tableau dashboard otherwise it is a byc dashboard
            const promiseArr = workbookIdArr.map(workbookId => Utils.isValidUuid(workbookId) ? this.client.removeWorkbookFromCommittee(committeeId, workbookId) : this.client.removeDashboardFromCommittee(committeeId, Number(workbookId)));
            yield Promise.all(promiseArr);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * addWorkbooksToCommittee(committeeId: number, workbookIdArr: string[]){
        this.isProcessing = true;
        try {
            const promiseArr = workbookIdArr.map(workbookId => Utils.isValidUuid(workbookId) ? this.client.addWorkbookToCommittee(committeeId, workbookId) : this.client.addDashboardToCommittee(committeeId, Number(workbookId)));
            yield Promise.all(promiseArr);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * updateFacultiesForCommittee(committeeId: number, adds: string[], removes: string[]): any {
        this.isUpdatingFacultiesForCommittee = true;
        try {
            yield this.client.updateFacultiesForCommittee(committeeId, adds, removes);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isUpdatingFacultiesForCommittee = false;
        }
    }

    setDashboardUpdateCount(addCount: number, removeCount: number){
        this.addMapCount = addCount;
        this.removeMapCount = removeCount;
    }

    resetAddedMapCount(){
        this.addMapCount = 0;
    }

    resetRemovedMapCount(){
        this.removeMapCount = 0;
    }
}

export { CommitteeStore };